import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const CreateCase = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [investigators, setInvestigators] = useState([]);
  const [flags, setFlags] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInvestigators();
    fetchFlags();
    fetchFacilities();
  }, []);

  const fetchInvestigators = async () => {
    try {
      const response = await api.get('/users/investigators');
      setInvestigators(response.data.data);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const fetchFlags = async () => {
    try {
      const response = await api.get('/flags/open');
      setFlags(response.data.data);
    } catch (error) {
      console.error('Error fetching flags:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to load facilities');
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    caseType: Yup.string().required('Case type is required'),
    priority: Yup.string().required('Priority is required'),
    description: Yup.string(),
    assignedTo: Yup.string(),
    facility: Yup.object({
      name: Yup.string(),
      id: Yup.string(),
      location: Yup.string(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      caseType: '',
      priority: 'Medium',
      status: 'Initial Review',
      assignedTo: '',
      facility: {
        name: '',
        id: '',
        location: '',
      },
      flagId: null,
      tags: [],
      teamMembers: [],
      isAdminViewOnly: false,
      dueDate: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Clean up values - remove empty strings and null values
        const cleanedValues = {
          title: values.title,
          description: values.description || undefined,
          caseType: values.caseType,
          priority: values.priority,
          status: values.status,
          assignedTo: values.assignedTo || undefined,
          tags: values.tags && values.tags.length > 0 ? values.tags : undefined,
        };
        
        // Only add flagId if it's a valid non-empty value
        if (values.flagId && values.flagId.trim && values.flagId.trim() !== '') {
          cleanedValues.flagId = values.flagId;
        }
        
        // Only add facility if it has a name
        if (values.facility && values.facility.name && values.facility.name.trim() !== '') {
          cleanedValues.facility = values.facility;
        }
        
        // Only add dueDate if provided
        if (values.dueDate) {
          cleanedValues.dueDate = values.dueDate;
        }

        // For Investigators: auto-assign to self and restrict priority
        if (user?.role === 'Investigator') {
          cleanedValues.assignedTo = user.id || user._id;
          if (cleanedValues.priority !== 'Medium' && cleanedValues.priority !== 'Low') {
            toast.error('Investigators can only self-assign Medium and Low priority cases');
            setLoading(false);
            return;
          }
        }

        // Add team members if provided
        if (values.teamMembers && values.teamMembers.length > 0) {
          cleanedValues.teamMembers = values.teamMembers;
        }

        // Add admin view only flag
        if (values.isAdminViewOnly) {
          cleanedValues.isAdminViewOnly = values.isAdminViewOnly;
        }
        
        // Remove undefined values
        Object.keys(cleanedValues).forEach(key => {
          if (cleanedValues[key] === undefined) {
            delete cleanedValues[key];
          }
        });
        const response = await api.post('/cases', cleanedValues);
        toast.success('Case created successfully');
        const caseId = response.data.data._id || response.data.data.id;
        if (caseId) {
          navigate(`/cases/${caseId}`);
        } else {
          console.error('Case ID not found in response:', response.data);
          toast.error('Case created but could not navigate to case details');
          navigate('/cases');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to create case');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFacilityChange = (e) => {
    const selectedFacilityId = e.target.value;
    if (selectedFacilityId) {
      const selectedFacility = facilities.find(f => f.id.toString() === selectedFacilityId);
      if (selectedFacility) {
        formik.setFieldValue('facility', {
          name: selectedFacility.name,
          id: selectedFacility.id.toString(),
          location: selectedFacility.address || '',
        });
      }
    } else {
      formik.setFieldValue('facility', {
        name: '',
        id: '',
        location: '',
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create New Case</h1>
        <p className="text-gray-600 mt-1">Fill in the details to create a new case</p>
      </div>

      <form onSubmit={formik.handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter case title"
          />
          {formik.touched.title && formik.errors.title && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter case description"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Case Type <span className="text-red-500">*</span>
            </label>
            <select
              name="caseType"
              value={formik.values.caseType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select case type</option>
              <option value="Stolen Pins">Stolen Pins</option>
              <option value="Suspicious Payments">Suspicious Payments</option>
              <option value="Contraband">Contraband</option>
              <option value="Escape">Escape</option>
              <option value="Chargebacks">Chargebacks</option>
              <option value="Staff (restricted)">Staff (restricted)</option>
              <option value="Other">Other</option>
            </select>
            {formik.touched.caseType && formik.errors.caseType && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.caseType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              name="priority"
              value={formik.values.priority}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={user?.role === 'Investigator'}
            >
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            {user?.role === 'Investigator' && (
              <p className="mt-1 text-xs text-gray-500">
                Investigators can only self-assign Medium and Low priority cases
              </p>
            )}
            {formik.touched.priority && formik.errors.priority && (
              <p className="mt-1 text-sm text-red-600">{formik.errors.priority}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assign To
          </label>
          {user?.role === 'Investigator' ? (
            <div>
              <input
                type="text"
                value={`${user.firstName} ${user.lastName} (You)`}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">
                As an Investigator, you can only self-assign Medium and Low priority cases
              </p>
            </div>
          ) : (
            <select
              name="assignedTo"
              value={formik.values.assignedTo}
              onChange={formik.handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select investigator</option>
              {investigators.map((investigator) => (
                <option key={investigator.id || investigator._id} value={investigator.id || investigator._id}>
                  {investigator.firstName} {investigator.lastName}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team Members (Optional)
          </label>
          <select
            multiple
            name="teamMembers"
            value={formik.values.teamMembers || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              formik.setFieldValue('teamMembers', selected);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px]"
          >
            {investigators.map((investigator) => (
              <option key={investigator._id} value={investigator._id}>
                {investigator.firstName} {investigator.lastName}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple team members</p>
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="isAdminViewOnly"
              checked={formik.values.isAdminViewOnly}
              onChange={formik.handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Admin View Only</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">Only Administrators and Supervisors can view this case</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Due Date
          </label>
          <input
            type="date"
            name="dueDate"
            value={formik.values.dueDate || ''}
            onChange={formik.handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min={new Date().toISOString().split('T')[0]}
          />
          <p className="mt-1 text-xs text-gray-500">
            A To-Do will be automatically created for the assigned investigator when a due date is set
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Related Flag (Optional)
          </label>
          <select
            name="flagId"
            value={formik.values.flagId || ''}
            onChange={(e) => formik.setFieldValue('flagId', e.target.value || null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select flag</option>
            {flags.map((flag) => (
              <option key={flag._id} value={flag._id}>
                {flag.flagNumber} - {flag.title}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facility Name
            </label>
            <select
              name="facility.name"
              value={formik.values.facility.id || ''}
              onChange={handleFacilityChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select facility</option>
              {facilities.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facility ID
            </label>
            <input
              type="text"
              name="facility.id"
              value={formik.values.facility.id}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              placeholder="Facility ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              name="facility.location"
              value={formik.values.facility.location}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              placeholder="Location"
            />
          </div>
        </div>

        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/cases')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating...' : 'Create Case'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCase;

